'use strict';
import { guuid, isObject, isString } from '@aretheyhappy/utils';
import KV from '../KV';
const MAX_WIDTH = 500;
const MAX_HEIGHT = 280;
async function getImageSize(file) {
    return new Promise((resolve, reject) => {
        const is_file = !!(file instanceof File);
        const fn = (src) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                return resolve({ width: img.width, height: img.height, src, img });
            };
            img.onerror = function (err) {
                return reject(err);
            };
            if (isString(src))
                img.src = src;
        };
        let src = false;
        if (!is_file) {
            if (isString(file))
                src = file;
            else if (isObject(file) && file.url)
                src = file.url;
            if (src !== false)
                fn(src);
        }
        else {
            const reader = new FileReader();
            //  Load file and then call function to get width/height
            reader.onload = evt => fn(evt.target !== null ? evt.target.result : null);
            // read the image file as a data URL.
            reader.readAsDataURL(file);
        }
    });
}
async function getVideoSize(file) {
    return new Promise((resolve, reject) => {
        const is_file = !!(file instanceof File);
        //  Create video element
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('crossOrigin', 'anonymous');
        video.onerror = function (err) {
            return reject(err);
        };
        //  Wait till video has a canplay event triggered
        video.addEventListener('loadeddata', () => {
            const { videoWidth, videoHeight } = video;
            video.pause();
            if (is_file)
                URL.revokeObjectURL(video.src);
            return resolve({ width: videoWidth, height: videoHeight, video });
        });
        //  Set src of video
        if (is_file)
            video.src = URL.createObjectURL(file);
        else if (isString(file))
            video.src = file;
        else if (isString(file.src))
            video.src = file.src;
        video.play();
    });
}
class UIThumbnailService {
    static getInstance() {
        if (!UIThumbnailService.instance) {
            UIThumbnailService.instance = new UIThumbnailService();
        }
        return UIThumbnailService.instance;
    }
    /**
     * Canvases are created on first use rather than in the constructor, so loading
     * mead before document.body exists (e.g. a <script> in <head>) doesn't throw.
     */
    mount() {
        if (this.parent && this.subparent)
            return;
        let uid = KV.get('uithumbnailsrv');
        if (!uid) {
            uid = guuid();
            //  Create canvas for rendering
            this.parent = document.createElement('canvas');
            this.parent.setAttribute('id', `s-${uid}`);
            this.parent.style.position = 'absolute';
            this.parent.style.display = 'none';
            this.parent.style.pointerEvents = 'none';
            this.parent.style.zIndex = '-1';
            document.body.appendChild(this.parent);
            //  Create subcanvas for rendering (used in different thumbnail styles)
            this.subparent = document.createElement('canvas');
            this.subparent.setAttribute('id', `s-${uid}-sub`);
            this.subparent.style.position = 'absolute';
            this.subparent.style.display = 'none';
            this.subparent.style.pointerEvents = 'none';
            this.subparent.style.zIndex = '-1';
            document.body.appendChild(this.subparent);
            KV.set('uithumbnailsrv', uid);
        }
        else {
            this.parent = document.getElementById(`s-${uid}`);
            this.subparent = document.getElementById(`s-${uid}-sub`);
        }
    }
    async drawThumbnail(el, width, height, has_alpha_layer = false, style = 'ratio') {
        this.mount();
        let ratio = 0;
        let downscaled_canvas_w = width || 0;
        let downscaled_canvas_h = height || 0;
        // Check if the current width is larger than the max
        if (downscaled_canvas_w > MAX_WIDTH) {
            ratio = MAX_WIDTH / downscaled_canvas_w;
            downscaled_canvas_h = downscaled_canvas_h * ratio;
            downscaled_canvas_w = downscaled_canvas_w * ratio;
        }
        // Check if current height is larger than max
        if (downscaled_canvas_h > MAX_HEIGHT) {
            ratio = MAX_HEIGHT / downscaled_canvas_h;
            downscaled_canvas_h = downscaled_canvas_h * ratio;
            downscaled_canvas_w = downscaled_canvas_w * ratio;
        }
        this.parent.width = downscaled_canvas_w;
        this.parent.height = downscaled_canvas_h;
        //  Draw image
        const ctx = this.parent.getContext('2d');
        if (ctx === null)
            return;
        ctx.drawImage(el, 0, 0, width || 0, height || 0, 0, 0, downscaled_canvas_w, downscaled_canvas_h);
        //  Specific avatar style
        if (style === 'avatar') {
            //  Get X and Y
            let sub_x = 0;
            let sub_y = 0;
            let des_w = downscaled_canvas_w;
            let des_h = downscaled_canvas_h;
            //  If width bigger than height -> calc x position and set desired width to desired height
            if (downscaled_canvas_w > downscaled_canvas_h) {
                sub_x = (downscaled_canvas_w * 0.5) - (downscaled_canvas_h * 0.5);
                des_w = des_h;
            }
            else if (downscaled_canvas_w < downscaled_canvas_h) {
                sub_y = (downscaled_canvas_h * 0.5) - (downscaled_canvas_w * 0.5);
                des_h = des_w;
            }
            const parentContext = this.parent.getContext('2d');
            if (parentContext === null)
                return;
            const img_raw = parentContext.getImageData(sub_x, sub_y, des_w, des_h);
            this.subparent.width = des_w;
            this.subparent.height = des_h;
            //  Draw image
            const sub_ctx = this.subparent.getContext('2d');
            if (sub_ctx === null)
                return;
            sub_ctx.putImageData(img_raw, 0, 0);
            //  Avatars are always jpeg
            return this.subparent.toDataURL('image/jpeg', 1);
        }
        else {
            return this.parent.toDataURL(has_alpha_layer ? 'image/png' : 'image/jpeg', 1);
        }
    }
    async getImageThumbnail(file, style = 'ratio') {
        try {
            //  Get image sizing
            const { width, height, src, img } = await getImageSize(file);
            if (img === undefined)
                return;
            const isImage = (file instanceof File) ? file.type === 'image/png' : false;
            //  Get thumbnail
            const thumbnail = await this.drawThumbnail(img, width, height, isImage, style);
            return { width, height, file, thumbnail, src };
        }
        catch {
            return false;
        }
    }
    async getVideoThumbnail(file, style = 'ratio') {
        try {
            //  Get video sizing
            const { width, height, video } = await getVideoSize(file);
            if (video === undefined)
                return false;
            //  Get thumbnail
            const thumbnail = await this.drawThumbnail(video, width, height, false, style);
            //  Video element cleanup (otherwise video keeps buffering)
            video.src = '';
            video.setAttribute('preload', 'none');
            return { width, height, file, thumbnail };
        }
        catch {
            return false;
        }
    }
}
const ThumbnailService = UIThumbnailService.getInstance();
export { ThumbnailService };
export default ThumbnailService;
//# sourceMappingURL=ThumbnailService.js.map